import { CompanyUserStatus } from '@/@types/enums/CompanyUserStatus';
import { isValidProductsReturnTo } from '@/helpers/productsUrl';

export const GUIDES_APP = 'guides';
/** `app` on the Snaplore plans and checkout, for a company that does not pay for Snaplore yet (SNAP-2241). */
export const SNAPLORE_APP = 'snaplore';

const STORAGE_KEY = 'snaploreAppContext';
const RETURN_TO_KEY = 'snaploreAppReturnTo';

type QueryLike = Record<string, unknown>;

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const getStoredAppContext = (): string | null => {
  return storage()?.getItem(STORAGE_KEY) ?? null;
};

export const setAppContext = (app: string): void => {
  storage()?.setItem(STORAGE_KEY, app);
};

export const clearAppContext = (): void => {
  storage()?.removeItem(STORAGE_KEY);
  storage()?.removeItem(RETURN_TO_KEY);
};

/**
 * Capture the app context (?app=guides) on an auth page.
 *
 * - explicit `?app=guides` → remember it for the rest of the auth journey
 *   (sessionStorage survives the OAuth roundtrip and in-app navigations)
 * - an organic visit (no app/redirect param and no auth token in the URL)
 *   resets any stale context so a later normal login is not hijacked
 * - token/social-callback/redirect visits keep whatever was captured earlier
 *
 * `hasUrlToken` means the token arrived IN THE URL (the `/login/:token` route
 * param), i.e. we are mid-handoff and the query params of the original
 * `?app=guides` entry are gone. It must NOT be set from an ambient
 * `SecureAuthToken` cookie: an already-signed-in user who simply opens /login
 * would then keep a stale guides context for the whole tab session and get
 * bounced into the Guides app instead of reaching Snaplore/billing (SNAP-2231
 * QA). sessionStorage outlives the journey, so failing to reset here is sticky.
 */
/**
 * A `redirect` that points back at an auth page is NOT a journey to preserve.
 * `AuthMiddleware` bounces a stale/expired token to `LOGIN` with
 * `redirect=<to.fullPath>`, which on the login route is `/login...` itself — so
 * a signed-out-but-stale user arrived carrying `?redirect=/login` and kept a
 * dead guides context, landing in Guides instead of Snaplore/billing after
 * signing in (SNAP-2231 QA, second-order case).
 */
const isAuthRouteRedirect = (redirect: unknown): boolean =>
  typeof redirect === 'string' && /^\/?#?\/?(login|signup)(\/|\?|$)/.test(redirect);

/**
 * Query for the LOGIN route when a dead session (401) is bounced back to login.
 *
 * A real deep link is kept as `redirect`. But when the dead session was already
 * ON an auth page (a stale cookie retried by LoginForm on `/login?app=guides`),
 * wrapping that page as `redirect=/login?app=guides` throws the journey away:
 * `captureAppContext` rightly ignores auth-page redirects, so the user got
 * "Sign in to Snaplore" and lost `returnTo` (SNAP-2231). Keep `app` and
 * `returnTo` as-is instead.
 */
export const deadSessionLoginQuery = (fullPath: string): Record<string, string> => {
  if (!isAuthRouteRedirect(fullPath)) return { redirect: fullPath };
  const params = new URLSearchParams(fullPath.split('?')[1] || '');
  const query: Record<string, string> = {};
  ['app', 'returnTo'].forEach((key) => {
    const value = params.get(key);
    if (value) query[key] = value;
  });
  return query;
};

export const captureAppContext = (query: QueryLike, options: { hasUrlToken?: boolean } = {}): void => {
  if (query?.app === GUIDES_APP) {
    setAppContext(GUIDES_APP);
    // Google sign-in comes back with no query, so the deep link has to wait in the tab as well.
    const returnTo = query.returnTo;
    if (isValidProductsReturnTo(returnTo)) storage()?.setItem(RETURN_TO_KEY, returnTo);
    else storage()?.removeItem(RETURN_TO_KEY);
    return;
  }
  const carriesJourney = !!query?.redirect && !isAuthRouteRedirect(query.redirect);
  if (!options.hasUrlToken && !carriesJourney && !query?.app) {
    clearAppContext();
  }
};

const appInLocation = (): string | null => {
  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex === -1) return null;
  return new URLSearchParams(hash.slice(queryIndex + 1)).get('app');
};

export const isGuidesContext = (query?: QueryLike): boolean => {
  return query?.app === GUIDES_APP || getStoredAppContext() === GUIDES_APP;
};

/** The validated deep link of a Guides journey: from the URL, or the one kept when the journey began. */
export const guidesReturnTo = (query?: QueryLike): string | undefined => {
  const returnTo = query?.returnTo;
  if (isValidProductsReturnTo(returnTo)) return returnTo;
  const stored = storage()?.getItem(RETURN_TO_KEY);
  return isValidProductsReturnTo(stored) ? stored : undefined;
};

/**
 * Query that keeps a Guides journey alive across an auth page or a domain hop: `app=guides`
 * plus the validated `returnTo` deep-link (an invalid one is dropped, open-redirect guard).
 */
export const guidesJourneyQuery = (query?: QueryLike): Record<string, string> => {
  const returnTo = guidesReturnTo(query);
  return {
    app: GUIDES_APP,
    ...(returnTo ? { returnTo } : {}),
  };
};

/**
 * Query suffix for the `/#/login` URL of another company's domain: the origin changes,
 * so sessionStorage (the Guides context) does not follow and everything the next login needs
 * has to travel in the URL — `redirect`, `targetCompanyId`, and in a Guides journey `app` plus
 * the validated `returnTo`.
 */
export const buildLoginHopSuffix = (query: QueryLike | undefined, isGuides: boolean): string => {
  const parts: string[] = [];
  const redirect = query?.redirect;
  if (typeof redirect === 'string' && redirect.startsWith('/')) {
    parts.push(`redirect=${encodeURIComponent(redirect)}`);
  }
  const targetCompanyId = query?.targetCompanyId;
  if (typeof targetCompanyId === 'string') {
    parts.push(`targetCompanyId=${encodeURIComponent(targetCompanyId)}`);
  }
  if (isGuides) {
    Object.entries(guidesJourneyQuery(query)).forEach(([key, value]) => {
      parts.push(`${key}=${encodeURIComponent(value)}`);
    });
  }
  return parts.length ? `?${parts.join('&')}` : '';
};

/**
 * The same, for a hop after a company switch: `session=1` tells the target that the API already set the session
 * cookie, so it finishes the login (SNAP-2309). Not for the page after sign-up, where nobody is signed in yet.
 */
export const buildSessionHopSuffix = (query: QueryLike | undefined, isGuides: boolean): string => {
  const suffix = buildLoginHopSuffix(query, isGuides);
  return suffix ? `?session=1&${suffix.slice(1)}` : '?session=1';
};

type CompanyLike = {
  isPaid?: boolean;
  enabledProducts?: string[] | null;
  /** Backend verdict (Roman 2026-11-03); absent on older backends. `kb` is Snaplore itself. */
  productAccess?: { howto?: boolean; kb?: boolean } | null;
} | null;

/**
 * Guides access is decided by the backend: `productAccess.howto` is true only when the
 * company pays for Guides itself or was granted it without payment (a KB subscription never
 * unlocks it). Every Guides decision goes through this, so they all agree.
 *
 * Fallback only when the field is absent (older backend), D1 (SNAP-2231/2241): signup grants
 * an entitlement (`enabledProducts`), payment gates it, so access is real only when BOTH are
 * true — a company paid for something else (e.g. Snaplore/kb) must NOT get free Guides access
 * just because it is paid at all (C2).
 */
export const hasGuidesAccess = (company: CompanyLike): boolean => {
  const verdict = company?.productAccess?.howto;
  if (typeof verdict === 'boolean') return verdict;
  return !!company?.isPaid && !!company.enabledProducts?.includes('howto');
};

/**
 * SNAP-2241 (Roman, 2026-11-03): "if user paid just for guides he can access just guides". A paid
 * company with Guides but not Snaplore itself, which the backend reports as `productAccess.kb === false`
 * (an older backend without the field never closes Snaplore). An unpaid company is not this case: it
 * keeps the not-paid flow, where it can still pay for Snaplore.
 */
export const paysOnlyForGuides = (company: CompanyLike): boolean =>
  !!company?.isPaid && company.productAccess?.kb === false && hasGuidesAccess(company);

/**
 * Whether the Snaplore controls should be hidden from this user: a company that pays only for Guides keeps a few
 * account and billing pages here, where search, create and the Snaplore menus would only answer "not in your plan".
 * A superadmin works in any company.
 */
export const isSnaploreClosedFor = (
  company: CompanyLike,
  user: { isSuperAdmin(): boolean } | null | undefined
): boolean => !!user && paysOnlyForGuides(company) && !user.isSuperAdmin();

/**
 * SNAP-2231: a company entitled to Guides but not to Snaplore (KB), i.e. a Guides
 * signup (`enabledProducts: ['howto']`; legacy companies default to `['kb']`).
 * Such a company is in Guides mode whether or not the URL carries `?app=guides`:
 * a plain login used to drop it into the Snaplore app it never bought, and its
 * plans page showed Snaplore prices.
 */
export const isGuidesOnlyCompany = (company: CompanyLike): boolean =>
  !!company?.enabledProducts?.includes('howto') &&
  !company.enabledProducts.includes('kb') &&
  // the backend treats meetings as the Snaplore tier too
  !company.enabledProducts.includes('meetings');

export type GuidesRedirectAction = 'products' | 'plans-upsell' | 'none';

/**
 * What a `?app=guides` visit should do once the active company is known.
 * Entitled + paid → straight into the Guides app. Paid for something else
 * (e.g. Snaplore/kb) → upsell to the Guides plans page instead of granting
 * free access or doing nothing (D2). Unpaid → 'none', falls through to the
 * existing unpaid-company billing flow (PaidCompanyMiddleware).
 */
export const guidesRedirectAction = (company: CompanyLike): GuidesRedirectAction => {
  if (!company) return 'none';
  if (hasGuidesAccess(company)) return 'products';
  if (company.isPaid) return 'plans-upsell';
  return 'none';
};

type GuidesEntitledLike =
  | {
      enabledProducts?: string[] | null;
      productAccess?: { howto?: boolean } | null;
      paddle?: { status?: string | null } | null;
      roles?: { user?: string | null; status?: string | null }[] | null;
      /** Whether the signed-in user owns this company. Absent on older backends. */
      isOwner?: boolean | null;
    }
  | null
  | undefined;

const PAID_PADDLE_STATUSES = ['active', 'trialing'];

/**
 * `/me` `companies[]` entries carry dotted keys (`'paddle.status'`), the current company the
 * nested object. Read both so the paid check does not silently see nothing.
 */
export const getCompanyPaddleStatus = (company: GuidesEntitledLike): string | null | undefined =>
  company?.paddle?.status ?? (company as Record<string, any> | null | undefined)?.['paddle.status'];

/** Whether a `/me` `companies[]` entry has a live subscription: its main one, or Guides bought on top of it. */
export const hasLiveSubscription = (company: GuidesEntitledLike): boolean => {
  const onTop = (company as { guidesSubscription?: { status?: string | null } | null } | null | undefined)
    ?.guidesSubscription;
  return [getCompanyPaddleStatus(company), onTop?.status].some((status) =>
    PAID_PADDLE_STATUSES.includes(status as never)
  );
};

/** The billing pages are for anyone who pays us, and a company with billing off can still have bought Guides. */
export const usesBillingPages = (
  company: (GuidesEntitledLike & { isBilling?: boolean }) | null | undefined
): boolean => {
  const onTop = (company as { guidesSubscription?: { status?: string | null } | null } | null | undefined)
    ?.guidesSubscription;
  return !!company?.isBilling || !!getCompanyPaddleStatus(company) || !!onTop?.status;
};

/**
 * H2: a multi-company login with `?app=guides` must not pick the first
 * PAID company (which may be paid for something else entirely) — prefer one
 * that actually has Guides access: the backend's `productAccess.howto`, or (older
 * backend) howto-entitled AND paid. An entitled-but-unpaid company the user owns is the fallback
 * so an unpaid Guides buyer still lands in their own company: only the owner can pay, and a member
 * would land on a dead end. A company the user was deactivated in is never picked: it is still listed
 * and the API lets the switch through, but every call there answers 403.
 */
export const pickGuidesCompany = <T extends GuidesEntitledLike>(
  companies: T[] | null | undefined,
  userId?: string | null
): T | null => {
  const usable = (companies ?? []).filter(
    (company) => !company?.roles?.some((role) => role.user === userId && role.status === CompanyUserStatus.DEACTIVATED)
  );
  const entitled = usable.filter((company) => company?.enabledProducts?.includes('howto'));
  const hasAccess = (company: T): boolean => {
    const verdict = company?.productAccess?.howto;
    return typeof verdict === 'boolean' ? verdict : PAID_PADDLE_STATUSES.includes(getCompanyPaddleStatus(company));
  };
  const withAccess = usable.find((company) => company?.productAccess?.howto === true);
  return withAccess ?? entitled.find(hasAccess) ?? entitled.find((company) => company?.isOwner !== false) ?? null;
};

export const SNAPLORE_PRODUCT_NAME = 'Snaplore';
export const GUIDES_PRODUCT_NAME = 'Guides';

/**
 * User-facing product name for the shared legacy pages.
 *
 * Guides reuses the legacy login / sign-up / billing screens as-is; the only
 * difference a Guides user should see is the product they are signing in to or
 * paying for. Pass the route query where it is available (auth pages), or omit
 * it on pages that only have the stored context.
 */
export const productDisplayName = (query?: QueryLike): string =>
  isGuidesContext(query) ? GUIDES_PRODUCT_NAME : SNAPLORE_PRODUCT_NAME;

/**
 * The checkout page lives on a different host (BILLING_HOSTNAME), so sessionStorage
 * does not carry over — there the guides context travels in the hash-router query.
 */
export const isGuidesInLocation = (): boolean => appInLocation() === GUIDES_APP;

/** The same, for a Snaplore checkout that a company paying for Guides started (SNAP-2241). */
export const isSnaploreInLocation = (): boolean => appInLocation() === SNAPLORE_APP;
